/**
 * Compare — derivation layer.
 *
 * Compare is no longer a two-version diff. It is the Workspace's statement
 * (P&L or GOP layout) with one column per SERIES the viewer adds — the
 * working version, last year's actual, the budget, last year's budget, or any
 * other version of the same year — and, optionally, a Δ column beside each
 * against whichever series is the baseline. "Version A vs Version B" is now
 * just the case where two versions are the two chips.
 *
 * Every figure is a total over the selected period — the fiscal year by
 * default, or any month range down to a single month. Sign follows the grid's conventions
 * (see analyticsData.ts): forecast values are display-positive already,
 * actuals arrive in transaction sign, budgets arrive expense-negative; other
 * versions come from calculateForecast, which returns display-positive
 * amounts. Nothing here fetches — the page hands in the version data.
 */

import type { ForecastAccount } from '../../types';
import { displayBudget } from '../workspace/columnRegistry';
import { buildGopLayout, normalizeDeptName, type GridLayout } from '../../utils/usaliLayout';

export const MONTH_KEYS = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];

export type SeriesId = 'current' | 'ly' | 'budget' | 'lyBudget' | `v:${number}`;

export interface CompareSeries {
  id: SeriesId;
  /** Chip / column caption */
  label: string;
  /** Short form for "Base vs X" variance captions */
  short: string;
  /** Small caps under the column caption: FORECAST / BUDGET / ACTUAL 2025 … */
  sub: string;
  swatch: string;
  note: string;
}

/** Per-account period totals for one added version: chartOfAccountId → total. */
export type VersionTotals = Map<number, number>;

export interface BuildCompareParams {
  accounts: ForecastAccount[];
  layout: GridLayout;
  series: CompareSeries[];
  /** Other versions' period totals by series id (`v:<id>`), already limited to `months`. */
  versionTotals: Map<string, VersionTotals>;
  /** 0-based month indexes in the period; omitted = the full fiscal year. */
  months?: number[];
  /**
   * Closed month keys ('jan'…) whose ACTUAL stands in for the working
   * forecast (the blended view). Omitted = the account's own displayed
   * values; an empty set = pure forecast in every month.
   */
  actualMonths?: Set<string>;
  sectionByDept: Map<string, string>;
  /** Drag-set department order (normalised name → order). */
  deptOrder?: Map<string, number>;
}

export type CompareRowKind = 'head' | 'leaf' | 'account' | 'total';

export interface CompareRow {
  key: string;
  name: string;
  kind: CompareRowKind;
  /** Account rows: the GL account, for jumping to it in the Workspace. */
  accountId?: number;
  /** Row this one drills out of: a department's band, an account's department. */
  parent?: string;
  indent: number;
  /** Expense-side row: a variance is favourable when the base is LOWER. */
  isExpense: boolean;
  /** Per-series period total, display-positive; totals are profit-signed. */
  values: Record<string, number>;
}

function normalizeActual(value: number, isExpense: boolean): number {
  return isExpense ? value : value * -1;
}

/** One account's period total on a series. */
export function accountTotal(account: ForecastAccount, seriesId: SeriesId, versionTotals: Map<string, VersionTotals>, months?: number[], actualMonths?: Set<string>): number {
  const isExpense = account.accountType !== 'Revenue';
  if (seriesId.startsWith('v:')) return versionTotals.get(seriesId)?.get(account.chartOfAccountId) ?? 0;
  let total = 0;
  const keys = months ? months.map((i) => MONTH_KEYS[i]) : MONTH_KEYS;
  for (const m of keys) {
    const x = account.monthlyExpanded?.[m];
    switch (seriesId) {
      case 'current':
        // Closed months: the viewer's blend choice wins over the version's.
        if (actualMonths?.has(m)) total += normalizeActual(x?.actual || 0, isExpense);
        else if (actualMonths && x?.isActualRolledIn) total += x.forecast ?? 0;
        else total += account.monthlyValues?.[m] ?? x?.current ?? x?.forecast ?? 0;
        break;
      case 'ly': total += normalizeActual(x?.lyActual || 0, isExpense); break;
      case 'budget': total += displayBudget(x?.budget || 0, isExpense); break;
      case 'lyBudget': total += displayBudget(x?.lyBudget || 0, isExpense); break;
      default: break;
    }
  }
  return total;
}

interface DeptGroup {
  department: string;
  isExpense: boolean;
  accounts: ForecastAccount[];
  order: number;
  firstNumber: string;
}

function groupDepartments(accounts: ForecastAccount[], isExpense: boolean, deptOrder?: Map<string, number>): DeptGroup[] {
  const groups = new Map<string, DeptGroup>();
  for (const a of accounts) {
    if ((a.accountType !== 'Revenue') !== isExpense) continue;
    const key = normalizeDeptName(a.department || 'Other');
    let g = groups.get(key);
    if (!g) {
      g = {
        department: (a.department || 'Other').trim(),
        isExpense,
        accounts: [],
        order: deptOrder?.get(key) ?? Number.MAX_SAFE_INTEGER,
        firstNumber: a.accountNumber || '',
      };
      groups.set(key, g);
    }
    g.accounts.push(a);
    if (a.accountNumber && a.accountNumber.localeCompare(g.firstNumber, undefined, { numeric: true }) < 0) g.firstNumber = a.accountNumber;
  }
  return Array.from(groups.values()).sort(
    (x, y) => x.order - y.order || x.firstNumber.localeCompare(y.firstNumber, undefined, { numeric: true }) || x.department.localeCompare(y.department)
  );
}

export function buildCompareRows({ accounts, layout, series, versionTotals, sectionByDept, deptOrder, months, actualMonths }: BuildCompareParams): CompareRow[] {
  const ids = series.map((s) => s.id);
  const zero = () => Object.fromEntries(ids.map((id) => [id, 0])) as Record<string, number>;
  const add = (into: Record<string, number>, from: Record<string, number>, sign = 1) => {
    ids.forEach((id) => { into[id] += sign * (from[id] || 0); });
  };
  const deptValues = (g: DeptGroup) => {
    const v = zero();
    g.accounts.forEach((a) => ids.forEach((id) => { v[id] += accountTotal(a, id, versionTotals, months, actualMonths); }));
    return v;
  };

  const revenue = groupDepartments(accounts, false, deptOrder);
  const expense = groupDepartments(accounts, true, deptOrder);
  const rows: CompareRow[] = [];

  // A department followed by its accounts — the drilldown's bottom level.
  const deptRows = (bandKey: string, g: DeptGroup, v: Record<string, number>): CompareRow[] => {
    const key = `${bandKey}:${normalizeDeptName(g.department)}`;
    const accountRows = [...g.accounts]
      .sort((x, y) => (x.accountNumber || '').localeCompare(y.accountNumber || '', undefined, { numeric: true }))
      .map((a) => ({
        key: `${key}:${a.chartOfAccountId}`,
        name: [a.accountNumber, a.accountName].filter(Boolean).join(' · '),
        kind: 'account' as const,
        accountId: a.chartOfAccountId,
        parent: key,
        indent: 32,
        isExpense: g.isExpense,
        values: Object.fromEntries(ids.map((id) => [id, accountTotal(a, id, versionTotals, months, actualMonths)])) as Record<string, number>,
      }));
    return [{ key, name: g.department, kind: 'leaf', parent: bandKey, indent: 14, isExpense: g.isExpense, values: v }, ...accountRows];
  };

  const pushBand = (key: string, name: string, groups: DeptGroup[], isExpense: boolean) => {
    const bandValues = zero();
    const leaves = groups.flatMap((g) => {
      const v = deptValues(g);
      add(bandValues, v);
      return deptRows(key, g, v);
    });
    rows.push({ key, name, kind: 'head', indent: 0, isExpense, values: bandValues });
    rows.push(...leaves);
    return bandValues;
  };
  const pushTotal = (key: string, name: string, values: Record<string, number>) =>
    rows.push({ key, name, kind: 'total', indent: 0, isExpense: false, values });

  if (layout === 'pnl') {
    const rev = pushBand('rev', 'Revenue', revenue, false);
    const exp = pushBand('exp', 'Expense', expense, true);
    const net = zero(); add(net, rev); add(net, exp, -1);
    pushTotal('net', 'Net Income', net);
    return rows;
  }

  const gop = buildGopLayout(revenue, expense, sectionByDept);
  const running = zero();
  add(running, pushBand('rev', 'Revenue', gop.revenue, false));
  add(running, pushBand('exp', 'Department Expense', gop.deptExpense, true), -1);
  pushTotal('todp', 'Departmental Profit', { ...running });

  const band = (key: string) => gop.costBands.find((b) => b.key === key);
  const absorb = (key: string, label: string) => {
    const b = band(key);
    if (!b) return false;
    // A band may hold a tagged income department; it nets against the costs.
    const bandValues = zero();
    const leaves = b.members.flatMap((m) => {
      const v = deptValues(m.dept as DeptGroup);
      add(bandValues, v, m.isExpense ? 1 : -1);
      return deptRows(key, { ...(m.dept as DeptGroup), isExpense: m.isExpense }, v);
    });
    rows.push({ key, name: label, kind: 'head', indent: 0, isExpense: true, values: bandValues });
    rows.push(...leaves);
    add(running, bandValues, -1);
    return true;
  };

  absorb('UND', 'Undistributed Expenses');
  pushTotal('gop', 'Gross Operating Profit', { ...running });
  if (absorb('MGMT', 'Management Fees')) pushTotal('ibfc', 'Income Before Fixed Charges', { ...running });
  const hasFixed = absorb('FIXED', 'Fixed Charges');
  if (hasFixed && band('BELOW')) pushTotal('ebitda', 'EBITDA', { ...running });
  absorb('BELOW', 'Interest, Depreciation & Other');
  if (gop.hasBelowGop) pushTotal('net', 'Net Income', { ...running });
  return rows;
}

/** Rows on screen: a row shows when every row it drills out of is open. */
export function visibleRows(rows: CompareRow[], collapsed: Set<string>, expanded: Set<string>): CompareRow[] {
  const byKey = new Map(rows.map((r) => [r.key, r]));
  // Bands start open, departments start closed.
  const isOpen = (r: CompareRow) => (r.kind === 'head' ? !collapsed.has(r.key) : expanded.has(r.key));
  return rows.filter((r) => {
    for (let p = r.parent ? byKey.get(r.parent) : undefined; p; p = p.parent ? byKey.get(p.parent) : undefined) {
      if (!isOpen(p)) return false;
    }
    return true;
  });
}

/** Favourable-oriented difference of `base` against `other` for a row. */
export function variance(row: CompareRow, baseId: string, otherId: string): number {
  return (row.values[baseId] || 0) - (row.values[otherId] || 0);
}

export function isFavourable(row: CompareRow, delta: number): boolean {
  return row.isExpense ? delta <= 0 : delta >= 0;
}

/** CSV of the visible columns. */
export function compareToCsv(rows: CompareRow[], series: CompareSeries[], baseId: string, showVariance: boolean): string {
  const esc = (v: string | number) => {
    const s = String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const header = ['Account'];
  series.forEach((s) => {
    header.push(s.label);
    if (showVariance && s.id !== baseId) header.push(`${series.find((b) => b.id === baseId)?.short || 'Base'} vs ${s.short}`);
  });
  const lines = [header.map(esc).join(',')];
  rows.forEach((r) => {
    const cells: (string | number)[] = [`${' '.repeat(r.kind === 'account' ? 4 : r.indent ? 2 : 0)}${r.name}`];
    series.forEach((s) => {
      cells.push(Math.round(r.values[s.id] || 0));
      if (showVariance && s.id !== baseId) cells.push(Math.round(variance(r, baseId, s.id)));
    });
    lines.push(cells.map(esc).join(','));
  });
  return lines.join('\n');
}
